import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { ROUTER_PATHS, TABLE_CONSTANTS_COLUMS } from '@/shared';
import {
  useDeleteGrupoSistema,
  useFetchGrupoSistemas,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { GrupoSistema } from '@/shared/interfaces';
import {
  emptyCellOneLevel,
  hasAllPermissions,
  hasPermission,
} from '@/shared/utils';
import { useUiConfirmModalStore } from '@/store/ui';

export type GruposSistemaPageProps = {};

const GruposSistemaPage: React.FC<GruposSistemaPageProps> = () => {
  useCheckPermissions(PermissionsEnum.auth_view_group);
  
  const returnUrlGruposSistemaPage =
    ROUTER_PATHS.administration.grupoSistemaNav;
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* mutations
  const deleteGrupoSistema = useDeleteGrupoSistema();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: GruposSistemaPagingRes,
    isLoading,
    isRefetching,
  } = useFetchGrupoSistemas({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      name: searchTerm,
      ...filterObject,
    },
  });

  ///* handlers
  const onEdit = (gruposistema: GrupoSistema) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar Grupo de Sistema',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlGruposSistemaPage}/editar/${gruposistema.id}`);
      },
    });
  };

  const onDelete = (gruposistema: GrupoSistema) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Grupo de Sistema',
      subtitle: '¿Está seguro que desea eliminar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        deleteGrupoSistema.mutate(gruposistema.id!);
      },
    });
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<GrupoSistema>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'id'),
      },

      {
        accessorKey: 'name',
        header: 'NOMBRE',
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'name'),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Grupos del Sistema"
      urlRedirect={`${returnUrlGruposSistemaPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.auth_add_group)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<GrupoSistema>
        columns={columns}
        data={GruposSistemaPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={GruposSistemaPagingRes?.data?.count}
        // // actions
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.auth_change_group,
          PermissionsEnum.auth_change_permission,
        ])}
        onEdit={onEdit}
        canDelete={hasPermission(PermissionsEnum.auth_delete_group)}
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default GruposSistemaPage;
